import React, { useState, useMemo } from 'react';
import {
  List, ExternalLink, ChevronUp, ChevronDown,
  ChevronLeft, ChevronRight, ReceiptText
} from 'lucide-react';
import TransactionDetailModal from '../../../../../components/TransactionDetailModal';

const TransactionsPanel = ({
  transactions,
  formatCurrency,
  formatDate,
  getTransactionTypeColor,
}) => {
  const [filterType, setFilterType] = useState('all');
  const [sortField, setSortField] = useState('date');
  const [sortDir, setSortDir] = useState('desc');
  const [page, setPage] = useState(1);
  const [selectedTx, setSelectedTx] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const perPage = 15;

  // Compute filter options
  const typeOptions = useMemo(() => {
    if (!transactions) return ['all'];
    const types = new Set(transactions.map(t => t.type?.toLowerCase()).filter(Boolean));
    return ['all', ...types];
  }, [transactions]);

  // Filter and sort
  const filtered = useMemo(() => {
    if (!transactions) return [];
    let data = [...transactions];
    if (filterType !== 'all') {
      data = data.filter(t => t.type?.toLowerCase() === filterType);
    }
    data.sort((a, b) => {
      let va, vb;
      switch (sortField) {
        case 'amount':
          va = Number(a.amount) || 0;
          vb = Number(b.amount) || 0;
          break;
        case 'type':
          va = (a.type || '');
          vb = (b.type || '');
          break;
        case 'status':
          va = (a.status || '');
          vb = (b.status || '');
          break;
        case 'date':
        default:
          va = new Date(a.createdAt || a.date || 0).getTime();
          vb = new Date(b.createdAt || b.date || 0).getTime();
          break;
      }
      return sortDir === 'asc' ? (va > vb ? 1 : -1) : (va < vb ? 1 : -1);
    });
    return data;
  }, [transactions, filterType, sortField, sortDir]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const paged = filtered.slice((page - 1) * perPage, page * perPage);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
    setPage(1);
  };

  const handleFilter = (type) => {
    setFilterType(type);
    setPage(1);
  };

  const handleRowClick = (tx) => {
    setSelectedTx(tx);
    setModalOpen(true);
  };

  // Prevent page from exceeding valid range when data changes
  React.useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <span className="sort-icon">↕</span>;
    return (
      <span className="sort-icon">
        {sortDir === 'asc' ? <ChevronUp size={10} style={{display:'inline'}} /> : <ChevronDown size={10} style={{display:'inline'}} />}
      </span>
    );
  };

  return (
    <div className="panel">
      {/* Toolbar */}
      <div className="tx-toolbar">
        <div className="tx-toolbar-left">
          <List /> Transactions
        </div>
        <div className="tx-toolbar-right">
          {typeOptions.map(t => (
            <button
              key={t}
              className={`filter-chip ${filterType === t ? 'active' : ''}`}
              onClick={() => handleFilter(t)}
            >
              {t === 'all' ? 'All' : t.charAt(0).toUpperCase() + t.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      {filtered.length > 0 ? (
        <>
          <div className="table-wrap">
            <table className="tx-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>User</th>
                  <th
                    className={sortField === 'type' ? 'sorted' : ''}
                    onClick={() => handleSort('type')}
                  >
                    Type <SortIcon field="type" />
                  </th>
                  <th
                    className={sortField === 'amount' ? 'sorted' : ''}
                    onClick={() => handleSort('amount')}
                  >
                    Amount <SortIcon field="amount" />
                  </th>
                  <th>Description</th>
                  <th
                    className={sortField === 'status' ? 'sorted' : ''}
                    onClick={() => handleSort('status')}
                  >
                    Status <SortIcon field="status" />
                  </th>
                  <th
                    className={sortField === 'date' ? 'sorted' : ''}
                    onClick={() => handleSort('date')}
                  >
                    Date <SortIcon field="date" />
                  </th>
                </tr>
              </thead>
              <tbody>
                {paged.map((tx) => (
                  <tr key={tx.transactionId || tx.id} onClick={() => handleRowClick(tx)}>
                    <td>
                      <span className="tx-id">
                        {(tx.transactionId || tx.id || '').slice(0, 12)}
                        <ExternalLink size={12} />
                      </span>
                    </td>
                    <td className="tx-user">
                      {tx.user?.uid || tx.userId || '—'}
                    </td>
                    <td>
                      <span className={`tag ${getTransactionTypeColor(tx.type)}`}>
                        {tx.type}
                      </span>
                    </td>
                    <td className="tx-amount">
                      {formatCurrency(tx.amount, tx.currency)}{' '}
                      <small style={{ opacity: 0.5, fontSize: '0.7em' }}>{tx.currency || 'USD'}</small>
                    </td>
                    <td className="tx-desc">
                      {tx.description || '—'}
                    </td>
                    <td>
                      <span className={`tag ${tx.status?.toLowerCase()}`}>
                        {tx.status}
                      </span>
                    </td>
                    <td className="tx-date">
                      {formatDate(tx.createdAt || tx.date)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="table-pagination">
              <button
                className="page-btn"
                disabled={page === 1}
                onClick={() => setPage(p => p - 1)}
              >
                <ChevronLeft size={14} />
              </button>
              <span className="page-label">
                Page {page} of {totalPages}
              </span>
              <button
                className="page-btn"
                disabled={page === totalPages}
                onClick={() => setPage(p => p + 1)}
              >
                <ChevronRight size={14} />
              </button>
            </div>
          )}
        </>
      ) : (
        <div className="state-msg">
          <ReceiptText />
          <h4>No transactions</h4>
          <p>
            {transactions && transactions.length > 0
              ? 'No transactions match the selected filter.'
              : 'Transactions will appear here once they occur.'}
          </p>
        </div>
      )}

      {/* Modal */}
      <TransactionDetailModal
        isOpen={modalOpen}
        transaction={selectedTx}
        onClose={() => { setModalOpen(false); setSelectedTx(null); }}
      />
    </div>
  );
};

export default TransactionsPanel;
